import { NegotiationDetail, NegotiationStatus } from 'api'
import React from 'react'
import { Validation, ValidationCellProps } from 'view/components/datagrid'
import { RowDataGetter } from 'view/shared/datagrid'
import { CORE_PRIMARY_6 } from 'view/styles/constants'
import { getIsFirstEnteredHaircutAndHaircutType, isFirstEnteredRate, ModificationType } from '../shared/deltaGetters'
import { getNegotiationModificationColor } from '../shared/utils'

type NegotiableCellRendererItemProps = Pick<NegotiationDetail, 'ownOrder' | 'negotiation'>

export interface NegotiableCellProps<T extends NegotiableCellRendererItemProps = NegotiableCellRendererItemProps>
  extends ValidationCellProps<T> {
  getModificationType: (detail: T) => ModificationType | null
  isRateField: boolean
  isHaircutOrHaircutTypeField: boolean
  rowDataGetter?: RowDataGetter<T>
  dataTestId?: string
}

export const Negotiable = <T extends NegotiableCellRendererItemProps = NegotiableCellRendererItemProps>(
  props: React.PropsWithChildren<NegotiableCellProps<T>>
) => {
  const detail = props.rowDataGetter ? props.rowDataGetter(props, true) : props.node.data

  const modificationColor = detail && getNegotiationModificationColor(props.getModificationType, detail)
  const isFirstRateValue = props.isRateField && detail ? isFirstEnteredRate(detail) : false
  const isFirstHaircutValue = props.isHaircutOrHaircutTypeField && detail ? getIsFirstEnteredHaircutAndHaircutType(detail) : false

  const isModified = !!modificationColor
  const isTargetRate =
    !!props.isRateField && !!detail && detail.negotiation.status === NegotiationStatus.REQUESTED_WITHOUT_PRICE && !isModified

  const style: React.CSSProperties = {
    ...((isFirstRateValue || isFirstHaircutValue) && { color: CORE_PRIMARY_6 }),
    ...((isFirstRateValue || isFirstHaircutValue) && { fontWeight: 'bold' }),
    ...(modificationColor && { borderBottomColor: modificationColor })
  }

  return (
    <Validation className={`negotiable-cell ${isModified ? 'modified' : ''} ${isTargetRate ? 'target-rate' : ''}`} style={style} {...props}>
      {props.children}
    </Validation>
  )
}

export const NegotiableCell = <T extends NegotiableCellRendererItemProps = NegotiableCellRendererItemProps>(
  props: NegotiableCellProps<T>
) => {
  return <Negotiable {...props}>{props.valueFormatted ?? props.value ?? ''}</Negotiable>
}
